import React from "react";
import {
    Button,
    ButtonGroup,
    Divider,
    Menu,
    MenuItem
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { TFunction } from "i18next";
import { useAvailableTestsAndTemplates } from "./hooks";
import { ILocalTest, testToLocalTest, templateToLocalTest } from "./helpers";
import ChooseResultModal from "./ChooseResultModal";

interface IAddTestToAssessmentLocalProps {
    existingTestNames: string[];
    onAddTest: (test: ILocalTest) => void;
    tLocal: TFunction;
}

const AddTestToAssessmentLocal = (props: IAddTestToAssessmentLocalProps) => {
    const { existingTestNames, onAddTest, tLocal } = props;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const divRef = React.useRef<HTMLDivElement>(null);

    interface IModalData {
        testData: {
            testId: string;
            testName: string;
            testType: CompleoShared.Assessments.testType;
            templateUUID?: string;
            compleoModelId?: string;
        };
        resultsList: CompleoShared.Assessments.IPossibleResults[];
    }
    const [modalData, setModalData] = React.useState<null | IModalData>(null);

    const {
        testOptions,
        templateOptions,
        ready
    } = useAvailableTestsAndTemplates(existingTestNames);

    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLElement>,
        option: {
            label: string;
            value: string;
            testType: CompleoShared.Assessments.testType;
            possibleResults?: CompleoShared.Assessments.IPossibleResults[];
            templateUUID?: string;
            fullData:
                | CompleoShared.Assessments.ITest
                | CompleoShared.Assessments.ITestTemplate;
        }
    ) => {
        event.stopPropagation();

        const testId = option.templateUUID || option.value;
        const testName = option.label;

        // If test type is answersToResults, need to show modal to choose result
        if (
            option.testType === "answersToResults" &&
            option.possibleResults &&
            option.possibleResults.length > 0
        ) {
            setModalData({
                testData: {
                    testId: testId,
                    testName: testName,
                    testType: option.testType,
                    templateUUID: option.templateUUID,
                    compleoModelId: option.templateUUID || undefined
                },
                resultsList: option.possibleResults
            });
            setAnchorEl(null);
        } else {
            // Add test directly
            let newTest: ILocalTest;
            if (option.templateUUID) {
                newTest = templateToLocalTest(
                    option.fullData as CompleoShared.Assessments.ITestTemplate,
                    option.templateUUID
                );
            } else {
                newTest = testToLocalTest(
                    option.fullData as CompleoShared.Assessments.ITest
                );
            }
            onAddTest(newTest);
            setAnchorEl(null);
        }
    };

    const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(divRef.current);
    };

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleAddTestWithResult = (test: ILocalTest) => {
        onAddTest(test);
    };

    const open = Boolean(anchorEl);

    if (!ready) {
        return null;
    }

    return (
        <div>
            <ButtonGroup
                variant="outlined"
                color="primary"
                aria-label="split button"
                ref={divRef}
                style={{ marginRight: 8 }}
            >
                <Button onClick={handleClickListItem}>
                    {tLocal("addTest")}
                </Button>
                <Button
                    color="primary"
                    size="small"
                    aria-controls={open ? "split-button-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label={tLocal("addTest") || "Add Test"}
                    aria-haspopup="menu"
                    onClick={handleClickListItem}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Menu
                id="add-test-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
            >
                <MenuItem
                    disabled
                    button={false}
                    style={{
                        color: testOptions.length > 0 ? undefined : "red"
                    }}
                >
                    {testOptions.length
                        ? tLocal("chooseATest")
                        : tLocal("noMoreTests")}
                </MenuItem>
                {testOptions.length > 0 && <Divider />}
                {testOptions.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={(event) => handleMenuItemClick(event, option)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    disabled
                    button={false}
                    style={{
                        color: templateOptions.length > 0 ? undefined : "red"
                    }}
                >
                    {templateOptions.length
                        ? tLocal("chooseATemplate")
                        : tLocal("noMoreTemplates")}
                </MenuItem>
                {templateOptions.length > 0 && <Divider />}
                {templateOptions.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={(event) => handleMenuItemClick(event, option)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
            {modalData && (
                <ChooseResultModal
                    open={modalData !== null}
                    resultsList={modalData.resultsList}
                    testData={modalData.testData}
                    handleClose={() => setModalData(null)}
                    handleAddTest={handleAddTestWithResult}
                    t={tLocal}
                />
            )}
        </div>
    );
};

export default AddTestToAssessmentLocal;
